//frontend/src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TabScrollButton from '@mui/material/TabScrollButton';
import axiosInstance from '../utils/axios';
import AppointmentsManager from '../components/dashboard/AppointmentsManager';
import SpecialistManagement from '../components/dashboard/SpecialistManagement';
import ClientsManagement from '../components/dashboard/ClientsManagement';
import UserSettings from '../components/dashboard/UserSettings';
import ServicesManagement from '../components/dashboard/ServicesManagement';
import SettingsManagement from '../components/dashboard/SettingsManagement';
import ProductsManagement from '../components/dashboard/ProductsManagement';
import { useTranslation } from 'react-i18next';
import { ArrowDropDown } from '@mui/icons-material';

const AdminTabs = ({ tabValue, setTabValue, filteredTabs, tabDefinitions, user, specialists, services, fetchSpecialists }) => {
   const [anchorEl, setAnchorEl] = useState(null);
   const [useDropdown, setUseDropdown] = useState(false);
   const tabsContainerRef = useRef(null);
   const { t } = useTranslation();

   useEffect(() => {
       const checkWidth = () => {
           if (tabsContainerRef.current) {
               const containerWidth = tabsContainerRef.current.offsetWidth;
               const tabsWidth = filteredTabs.length * 150;
               setUseDropdown(tabsWidth > containerWidth);
           }
       };
       checkWidth();
       window.addEventListener('resize', checkWidth);
       return () => window.removeEventListener('resize', checkWidth);
   }, [filteredTabs]);

   const handleClick = (event) => {
       setAnchorEl(event.currentTarget);
   };

   const handleClose = (index) => {
       if (typeof index === 'number') {
           setTabValue(index);
       }
       setAnchorEl(null);
   };

   return (
       <Box>
           <Box ref={tabsContainerRef} sx={{ width: '100%' }}>
               {useDropdown ? (
                   <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                       <Button
                           onClick={handleClick}
                           endIcon={<ArrowDropDown />}
                           sx={{
                               minHeight: 48,
                               px: 2,
                               borderRadius: 0,
                               borderBottom: 2,
                               borderColor: 'primary.main',
                               color: 'primary.main',
                               '&:hover': {
                                   backgroundColor: 'rgba(0, 0, 0, 0.04)'
                               }
                           }}
                       >
                           {filteredTabs[tabValue]?.label}
                       </Button>
                       <Menu
                           anchorEl={anchorEl}
                           open={Boolean(anchorEl)}
                           onClose={() => handleClose()}
                           sx={{
                               '& .MuiPaper-root': {
                                   minWidth: 200,
                                   maxHeight: 300
                               }
                           }}
                       >
                           {filteredTabs.map((tab, index) => (
                               <MenuItem 
                                   key={tab.key}
                                   onClick={() => handleClose(index)}
                                   selected={index === tabValue}
                                   sx={{
                                       minHeight: 40,
                                       px: 2,
                                       '&.Mui-selected': {
                                           backgroundColor: 'primary.light',
                                           '&:hover': {
                                               backgroundColor: 'primary.light'
                                           }
                                       }
                                   }}
                               >
                                   {tab.label}
                               </MenuItem>
                           ))}
                       </Menu>
                   </Box>
               ) : (
                   <Tabs 
                       value={tabValue} 
                       onChange={(e, newValue) => setTabValue(newValue)}
                       sx={{ borderBottom: 1, borderColor: 'divider' }}
                   >
                       {filteredTabs.map((tab, index) => (
                           <Tab 
                               key={tab.key} 
                               label={tab.label}
                               sx={{
                                   minHeight: 48,
                                   textTransform: 'none',
                                   fontWeight: 'medium'
                               }}
                           />
                       ))}
                   </Tabs>
               )}
           </Box>
           
           <Box sx={{ mt: 3 }}>
               {filteredTabs[tabValue] && 
                   React.createElement(
                       tabDefinitions.find(def => def.key === filteredTabs[tabValue].key).component,
                       {
                           isSuperAdmin: user.role === 'superadmin',
                           specialists,
                           services,
                           onUpdate: fetchSpecialists,
                           user: user
                       }
                   )
               }
           </Box>
       </Box>
   );
};

function Dashboard() {
   const { t } = useTranslation();
   const [user, setUser] = useState(null);
   const [specialists, setSpecialists] = useState([]);
   const [services, setServices] = useState([]);
   const [tabValue, setTabValue] = useState(0);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [hiddenTabs, setHiddenTabs] = useState({});
   const navigate = useNavigate();

   const tabDefinitions = [
       { key: 'appointments', component: AppointmentsManager },
       { key: 'specialists', component: SpecialistManagement },
       { key: 'services', component: ServicesManagement },
       { key: 'clients', component: ClientsManagement },
       { key: 'products', component: ProductsManagement },
       { key: 'settings', component: SettingsManagement }
   ];

   useEffect(() => {
    const fetchTabSettings = async () => {
        try {
            const response = await axiosInstance.get('/settings/tabs');
            setHiddenTabs({
                hiddenTabs: response.data.hiddenTabs || {},
                tabOrder: response.data.tabOrder,
                columnSettings: response.data.columnSettings
            });
        } catch (error) {
            console.error('Error:', error);
        }
    };
       fetchTabSettings();
   }, []);

   useEffect(() => {
       const checkAuthAndFetchData = async () => {
           const token = localStorage.getItem('token');
           if (!token) {
               navigate('/login');
               return;
           }
           try {
               await fetchUserData();
           } catch (error) {
               if (error.response?.status === 401) {
                   localStorage.removeItem('token');
                   navigate('/login');
               }
           } finally {
               setLoading(false);
           }
       };

       checkAuthAndFetchData();
   }, [navigate]);

   useEffect(() => {
       if (user?.role === 'superadmin') {
           fetchSpecialists();
           fetchServices();
       }
   }, [user]);

   const fetchUserData = async () => {
       try {
           const response = await axiosInstance.get('/auth/profile');
           setUser(response.data);
       } catch (error) {
           setError(error.message);
           throw error;
       }
   };

   const fetchSpecialists = async () => {
       try {
           const response = await axiosInstance.get('/specialists');
           setSpecialists(response.data);
       } catch (error) {
           console.error('Error fetching specialists:', error);
       }
   };

   const fetchServices = async () => {
       try {
           const response = await axiosInstance.get('/services');
           setServices(response.data);
       } catch (error) {
           console.error('Error fetching services:', error);
       }
   };

   const handleLogout = () => {
       localStorage.removeItem('token');
       localStorage.removeItem('userRole');
       navigate('/login');
   };

   const renderContent = () => {
    switch (user?.role) {
        case 'superadmin':
case 'admin':
    const orderedTabs = hiddenTabs.tabOrder || [
        'appointments', 'specialists', 'services', 
        'clients', 'products', 'settings'
    ];
    
    const visibleTabs = orderedTabs.map(key => ({
        key,
        label: t(`dashboard.tabs.${key}`),
        hidden: key === 'settings' ? false : !!hiddenTabs.hiddenTabs?.[key]
    }));

    const filteredTabs = visibleTabs.filter(tab => !tab.hidden);

               return (
                   <AdminTabs 
                       tabValue={tabValue}
                       setTabValue={setTabValue}
                       filteredTabs={filteredTabs}
                       tabDefinitions={tabDefinitions}
                       user={user}
                       specialists={specialists}
                       services={services}
                       fetchSpecialists={fetchSpecialists}
                   />
               );
           case 'specialist':
               return (
                   <Box>
                       <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                           <Tab label={t('common.myappointments')} />
                           <Tab label={t('common.myclients')} />
                           <Tab label={t('common.settings')} />
                       </Tabs>
                       {tabValue === 0 && (
                           <AppointmentsManager 
                               isSuperAdmin={false} 
                               specialistId={user._id}
                               user={user}
                           />
                       )}
                       {tabValue === 1 && <ClientsManagement specialistId={user._id} user={user} />}
                       {tabValue === 2 && <UserSettings user={user} onUpdate={fetchUserData} />}
                   </Box>
               );
           default:
               return (
                   <Box>
                       <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                           <Tab label={t('common.myappointments')} />
                           <Tab label={t('common.settings')} />
                       </Tabs>
                       {tabValue === 0 && (
                           <AppointmentsManager 
                               isSuperAdmin={false} 
                               userId={user._id}
                               user={user}
                           />
                       )}
                       {tabValue === 1 && <UserSettings user={user} onUpdate={fetchUserData} />}
                   </Box>
               );
       }
   };

   if (loading) {
       return (
           <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
               <CircularProgress />
           </Container>
       );
   }

   if (error) {
       return (
           <Container>
               <Alert severity="error">{error}</Alert>
           </Container>
       );
   }

   return (
       <Container maxWidth="lg">
           <Box sx={{ mb: 4 }}>
               <Box 
                   display="flex" 
                   justifyContent="space-between" 
                   alignItems="center"
                   p={2}
                   bgcolor="background.paper"
                   borderRadius={1}
                   boxShadow={1}
               >
                   <Box>
                       <Typography variant="h4" component="h1">
                           {t('common.dashboard')}
                       </Typography>
                       <Typography variant="subtitle1" color="textSecondary">
                           {user?.role.charAt(0).toUpperCase() + user?.role.slice(1)} - {user?.name}
                       </Typography>
                   </Box>
                   <Button 
                       onClick={handleLogout}
                       variant="outlined"
                       color="error"
                       startIcon={<LogoutIcon />}
                   >
                       {t('common.logout')}
                   </Button>
               </Box>
           </Box>

           <Box sx={{ 
               bgcolor: 'background.paper',
               borderRadius: 1,
               boxShadow: 1,
               p: 2
           }}>
               {renderContent()}
           </Box>
       </Container>
   );
}

export default Dashboard;